import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RcFile } from 'antd/lib/upload/interface';
import { RequestStatus, REQUEST_STATUSES } from 'commons/types/common';
import type { Document, DocumentType } from 'commons/types/documentsTypes';
import type { RootState } from 'stores/store';
import {
  createDocument,
  deleteDocument,
  getDocument,
  getDocuments,
} from './documentsClient';

export interface DocumentsState {
  list?: Document[];
  createRequestStatus?: RequestStatus;
  deleteRequestStatus?: RequestStatus;
  fetchDocumentRequestStatus?: RequestStatus;
  listRequestStatus?: RequestStatus;
}

const initialState: DocumentsState = {};

export const createDocumentAsync = createAsyncThunk(
  'documents/createCurrent',
  ({ document, type }: { document: RcFile; type: DocumentType }) =>
    createDocument(document, type),
);

export const getDocumentAsync = createAsyncThunk(
  'documents/get',
  (documentID: string) => getDocument(documentID),
);

export const deleteDocumentAsync = createAsyncThunk(
  'documents/deleteCurrent',
  (documentID: number) => deleteDocument(documentID),
);

export const getDocumentsAsync = createAsyncThunk(
  'documents/getList',
  (params: URLSearchParams = new URLSearchParams()) => getDocuments(params),
);

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    resetCurrentDocument: (state) => {
      delete state.createRequestStatus;
      delete state.deleteRequestStatus;
    },
    resetDocumentsList: (state) => {
      delete state.list;
      delete state.listRequestStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDocumentAsync.pending, (state) => {
        state.createRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(createDocumentAsync.fulfilled, (state, { payload }) => {
        state.createRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.list?.push(payload);
      })
      .addCase(createDocumentAsync.rejected, (state) => {
        state.createRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(deleteDocumentAsync.pending, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(deleteDocumentAsync.fulfilled, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(deleteDocumentAsync.rejected, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getDocumentsAsync.pending, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getDocumentsAsync.fulfilled, (state, { payload }) => {
        state.listRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.list = payload;
      })
      .addCase(getDocumentsAsync.rejected, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getDocumentAsync.pending, (state) => {
        state.fetchDocumentRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getDocumentAsync.fulfilled, (state, { payload }) => {
        state.fetchDocumentRequestStatus = REQUEST_STATUSES.SUCCESS;
        window.open(payload?.uri, '_blank');
      })
      .addCase(getDocumentAsync.rejected, (state) => {
        state.fetchDocumentRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const { resetCurrentDocument, resetDocumentsList } =
  documentsSlice.actions;

export const getDocumentsList = (state: RootState) => state.documents.list;

export const getCreateDocumentRequestStatus = (state: RootState) =>
  state.documents.createRequestStatus;
export const getDeleteDocumentRequestStatus = (state: RootState) =>
  state.documents.deleteRequestStatus;
export const getDocumentsListRequestStatus = (state: RootState) =>
  state.documents.listRequestStatus;

export default documentsSlice.reducer;
